import {assertUnreachable} from '@wandb/weave/common/util/types';
import {toast} from 'react-toastify';
import {create} from 'zustand';

import {GenericWebhook} from '../../../Webhooks/WebhooksTable';
import {JobDetails} from '../../JobDetail/utilTypes';
import {QueueOption} from '../../SelectQueue';
import {getNotificationConfig, NotificationConfig} from '../notificationUtils';
import {getWebhookActionDetails} from '../TriggersTable/utilData';
import {TriggeredActionType} from '../TriggersTable/utilTypes';
import {CreateTriggerDrawerInit} from './EventStore';
import {JobOption} from './types';

export type JobConfig = {
  targetProjectName: string | undefined;
  sourceProjectName: string | undefined;
  queuedJob: JobDetails | null;
  queuedJobAlias: string | null;
  queuedJobID: JobOption | null;
  overrides: string;
  overridesError: string;
  selectedQueue: QueueOption | null;
};

export type WebhookConfig = {
  webhook: GenericWebhook | undefined;
  webhookPayload: string;
  webhookPayloadError: string;
};

/*
A quick note on the zustand store here: it is not as good of an example as EventStore.tsx,
so I'd encourage you to look at that file if you just want to learn about zustand.

The big difference is that this is just managing state, with business logic in the components,
but we'd prefer to move more logic into the store's actions.
*/

export type ActionConfig = {
  doAsyncInit: (
    metricChangeNotificationsEnabled: boolean,
    initProps: CreateTriggerDrawerInit,
    onClose: () => void
  ) => Promise<void>;
  actionType: TriggeredActionType | undefined;
  // Note that the entityName is also present in the EventConfig store.
  // Why repeat? We want to allow the user to configure the entity where they
  // listen to events and the entit where they trigger actions separately.
  // this one *is* a value we expect users to change (sometimes) as
  // they configure the automation.
  entityName: string | undefined;
  setEntityName: (entityName: string | undefined) => void;

  job: JobConfig;
  updateJobConfig: (jobConfig: Partial<JobConfig>) => void;
  webhook: WebhookConfig;
  updateWebhookConfig: (webhookConfig: Partial<WebhookConfig>) => void;
  notification: NotificationConfig;
  updateNotificationConfig: (
    notificationConfig: Partial<NotificationConfig>
  ) => void;

  setActionType: (actionType: TriggeredActionType) => void;
  metricChangeNotificationsEnabled: boolean;
};

// make sure we return a new object each time - need new copies of all
// the child objects
const getInitialActionConfig = () => {
  return {
    entityName: undefined,
    job: {
      targetProjectName: undefined,
      sourceProjectName: undefined,
      queuedJob: null,
      queuedJobAlias: null,
      queuedJobID: null,
      overrides: '',
      overridesError: '',
      selectedQueue: null,
    },
    actionType: undefined,
    webhook: {
      webhook: undefined,
      webhookPayload: '{\n\n\n\n\n}',
      webhookPayloadError: '',
    },
    notification: {
      teamSlackIntegration: undefined,
      entityName: undefined,
    },
    metricChangeNotificationsEnabled: false,
  };
};

export const useActionConfigStore = create<ActionConfig>()((set, get) => ({
  ...getInitialActionConfig(),
  doAsyncInit: async (
    metricChangeNotificationsEnabled: boolean,
    initProps: CreateTriggerDrawerInit,
    onClose: () => void
  ) => {
    set({
      ...getInitialActionConfig(),
      metricChangeNotificationsEnabled,
      entityName: initProps.actionEntityName,
      actionType: initProps.defaultAction,
    });
    if (initProps.isUpdating) {
      await ActionStoreActions.private.initForEditTrigger(initProps, onClose);
    }
  },
  setEntityName: (entityName: string | undefined) => {
    set({
      // reset since config will be different when changing entity
      ...getInitialActionConfig(),
      entityName,
      // these should be persisted from previous state
      metricChangeNotificationsEnabled: get().metricChangeNotificationsEnabled,
      actionType: get().actionType,
    });
  },
  updateJobConfig: (newJob: Partial<JobConfig>) =>
    set(state => ({job: {...state.job, ...newJob}})),
  updateWebhookConfig: (newWebhook: Partial<WebhookConfig>) =>
    set(state => ({webhook: {...state.webhook, ...newWebhook}})),
  updateNotificationConfig: (newNotification: Partial<NotificationConfig>) =>
    set(state => ({notification: {...state.notification, ...newNotification}})),
  setActionType: (actionType: TriggeredActionType) => {
    if (!get().entityName) {
      console.error('entityName is required before setting actionType');
    }
    set({actionType});
    switch (actionType) {
      case 'NotificationTriggeredAction':
      case 'GenericWebhookTriggeredAction':
      case 'QueueJobTriggeredAction':
        // these don't need any init
        break;
      default:
        assertUnreachable(actionType);
    }
  },
}));

const actionSet = useActionConfigStore.setState;
const actionGet = useActionConfigStore.getState;

export const ActionStoreActions = {
  private: {
    initForEditTrigger: async (
      initProps: CreateTriggerDrawerInit,
      onClose: () => void
    ) => {
      if (!initProps.isUpdating) {
        throw new Error('initForEditTrigger called with isUpdating: false');
      }
      if (!initProps.trigger.triggeredAction) {
        throw new Error('initForEditTrigger called with no triggeredAction');
      }
      switch (initProps.trigger.triggeredAction.actionType) {
        case 'GenericWebhookTriggeredAction': {
          const {
            webhook,
            actionConfigString,
            entityName: webhookEntityName,
          } = getWebhookActionDetails(initProps.trigger);
          actionSet({
            actionType: 'GenericWebhookTriggeredAction',
            webhook: {
              webhook: webhook,
              webhookPayload: actionConfigString,
              webhookPayloadError: '',
            },
            entityName: webhookEntityName,
          });
          break;
        }
        case 'NotificationTriggeredAction': {
          const {teamSlackIntegration, entityName: slackEntityName} =
            getNotificationConfig(initProps.trigger);

          actionSet({
            actionType: 'NotificationTriggeredAction',
            notification: {
              teamSlackIntegration,
            },
            entityName: slackEntityName,
          });
          break;
        }
        case 'QueueJobTriggeredAction': {
          toast.error(
            'We do not currently support editing Launch job automations'
          );
          onClose();
          throw new Error(
            'We do not currently support editing Launch job automations'
          );
        }
        case undefined:
          toast.error(
            'There was an unexpected error loading this automation. Please contact support if you see this repeatedly.'
          );
          return;
        default:
          assertUnreachable(initProps.trigger.triggeredAction.actionType);
      }
    },
  },
};
// we used to validate the that queue.value != '', but that is the value used
// for the starter queue option, so we don't want to reject that.
export const isValidQueue = (queue: QueueOption | null) => queue != null;

export const isValidWebhookConfig = (config: WebhookConfig) => {
  return (
    config.webhook != null &&
    config.webhook.id != null &&
    config.webhook.id !== '' &&
    config.webhookPayload != null &&
    config.webhookPayloadError === ''
  );
};

export const isValidNotificationConfig = (config: NotificationConfig) => {
  return (
    config.teamSlackIntegration != null &&
    config.teamSlackIntegration.channelName != null &&
    config.teamSlackIntegration.channelName !== '' &&
    config.teamSlackIntegration.id != null &&
    config.teamSlackIntegration.id !== ''
  );
};

export const isValidActionConfig = (actionConfig: ActionConfig) => {
  switch (actionConfig.actionType) {
    case 'QueueJobTriggeredAction':
      return (
        actionConfig.job.queuedJob != null &&
        actionConfig.job.targetProjectName != null &&
        actionConfig.job.sourceProjectName != null &&
        isValidQueue(actionConfig.job.selectedQueue) &&
        actionConfig.job.overridesError === ''
      );
    case 'GenericWebhookTriggeredAction':
      return isValidWebhookConfig(actionConfig.webhook);
    case 'NotificationTriggeredAction':
      return isValidNotificationConfig(actionConfig.notification);
    case undefined: // occurs when user has not selected an action type
      return false;
    default:
      assertUnreachable(actionConfig.actionType);
  }
};
